import api from '../api';
import { ApiError } from './types';

export interface PixPaymentRequest {
  pedido_id: number;
  valor: number;
}

export interface CardPaymentRequest {
  pedido_id: number;
  valor: number;
  cartao: {
    numero: string;
    nome: string;
    validade: string;
    cvv: string;
    cpf: string;
  };
}

export interface PixPaymentResponse {
  success: boolean;
  message: string;
  payment_data?: {
    qr_code?: string;
    qr_code_text?: string;
    transaction_id?: string;
    expires_at?: string;
    manual?: boolean;
    pix_cnpj?: string;
  };
  error?: string;
}

export interface CardPaymentResponse {
  success: boolean;
  transaction_id?: string;
  message: string;
  payment_data?: {
    transaction_id?: string;
    cardToken?: string;
    responseCode?: string;
    authorizationCode?: string;
  };
}

export class PagamentosService {
  /**
   * Gera PIX com retry (2 tentativas)
   * @param pedidoId - ID do pedido
   * @param valor - Valor do pagamento
   * @param tentativa - Número da tentativa atual (padrão: 1)
   * @returns Promise com resposta do PIX
   */
  static async gerarPixComRetry(
    pedidoId: number,
    valor: number,
    tentativa: number = 1
  ): Promise<PixPaymentResponse> {
    try {
      console.log(`🔄 Tentativa ${tentativa} de gerar PIX para pedido ${pedidoId}`);

      const headers: any = {};

      // Verificar se há simulação de erro configurada
      if (typeof window !== 'undefined') {
        const simulateError = localStorage.getItem('SIMULATE_PIX_ERROR');
        if (simulateError === 'true') {
          headers['x-simulate-pix-error'] = 'true';
        }
      }

      const response = await api.post<PixPaymentResponse>('pagamentos/pix', {
        pedido_id: pedidoId,
        valor: valor,
      }, { headers });

      if (response.data.success && response.data.payment_data) {
        console.log('✅ PIX gerado com sucesso na tentativa', tentativa);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Erro ao gerar PIX');
      }
    } catch (error: any) {
      console.error(`❌ Erro na tentativa ${tentativa}:`, error);

      // Se ainda há tentativas, tentar novamente
      if (tentativa < 2) {
        console.log(`🔄 Tentando novamente... (tentativa ${tentativa + 1})`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Aguardar 1 segundo antes de tentar novamente
        return this.gerarPixComRetry(pedidoId, valor, tentativa + 1);
      }

      // Se esgotou as tentativas, retornar erro
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Erro ao gerar PIX após 2 tentativas',
        message: error.response?.data?.message || error.message || 'Erro ao gerar PIX após 2 tentativas',
      };
    }
  }

  /**
   * Processa pagamento com cartão de crédito
   * @param cardPaymentRequest - Dados do pagamento com cartão
   * @returns Promise com resposta do pagamento
   */
  static async processarCartao(
    cardPaymentRequest: CardPaymentRequest
  ): Promise<CardPaymentResponse> {
    try {
      const response = await api.post<CardPaymentResponse>(
        'pagamentos/cartao',
        cardPaymentRequest
      );

      return response.data;
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Trata erros da API
   */
  private static handleApiError(error: any): ApiError {
    if (error.response?.data) {
      return {
        message: error.response.data.message || error.message || 'Erro na requisição',
        statusCode: error.response.status || error.statusCode || 500,
        error: error.response.data.error || error.error || 'Unknown Error',
      };
    }

    if (error.message) {
      return {
        message: error.message,
        statusCode: 500,
        error: 'Network Error',
      };
    }

    return {
      message: 'Erro de conexão com o servidor',
      statusCode: 500,
      error: 'Network Error',
    };
  }
}

